import { Box, Divider, styled, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { LwButton } from 'redesign';
import { useGetDepartmentDetail } from 'api/hooks/department/use-get-department-detail';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { useGetWorkCreateData, useGetWorkTemplate, useGetWorkTemplates } from 'api';
import { useSiteDetection } from 'shared/hooks';
import { useEffect, useMemo } from 'react';
import { useGetShiftDetail } from 'api/hooks/shift/get-shift-detail';
import { useTranslation } from 'react-i18next';
import { WorkerEmploymentType } from '@types';
import { useHideLimitedCompanyShiftFields } from 'ogp/limited-company-capabilities';
import { WorkForm } from './work-form';
import { WorkFormCompanyInfo, WorkFormContext, WorkFormMode } from './work-form.types';
import {
  applyLimitedCompanyDepartmentDefaults,
  mapShiftDetailToForm,
  mapWorkTemplateToForm,
} from './helpers/mappers';
import { useWorkFormTexts } from './hooks/use-work-form-texts';
import { useWorkFormMutations } from './hooks/use-work-form-mutations';
import { useMultiDaySubmitValidation } from './multi-day/hooks/use-multi-day-submit-validation';
import { useWorkFormCreationTabs } from './hooks/use-work-form-creation-tabs';
import { WorkFormCreationTabs } from './components/work-form-creation-tabs';
import { changeWorkFormCreateTab } from './multi-day/work-form-create-tabs/use-work-form-tab-reset';
import { WorkFormCreationType } from './work-form-creation-type.types';
import { MULTI_DAY_WORK_FORM_FIELDS, WorkFormCreateTab } from './multi-day/multi-day-work.types';
import { getHelperTexts } from '../shift-forms/components/forms/shared/get-helper-texts/get-helper-texts';
import { RenderUnsettledUI } from '../render-unsettled-ui';

export type WorkFormProviderProps = {
  cancel: () => void;
  companies: WorkFormCompanyInfo[];
  formMode: WorkFormMode;
  selectedShiftIds: string[];
  isHqAdmin: boolean;
  onSuccess: (shiftId: string) => void;
  selectBorrowerCompany?: boolean;
  ownCompanyId?: string;
};

export type WorkFormDataProviderProps = {
  cancel: () => void;
  companies: WorkFormCompanyInfo[];
  formMode: WorkFormMode;
  isHqAdmin: boolean;
  onSuccess: (shiftId: string) => void;
  shiftId: string | undefined;
  shiftData: ShiftDetail | undefined;
  selectBorrowerCompany?: boolean;
  ownCompanyId?: string;
};

export const WorkFormProvider = ({
  cancel,
  companies,
  formMode,
  selectedShiftIds,
  isHqAdmin,
  onSuccess,
  selectBorrowerCompany = false,
  ownCompanyId,
}: WorkFormProviderProps) => {
  const shiftId = formMode != 'create' ? selectedShiftIds[0] : undefined;
  const shiftQuery = useGetShiftDetail(shiftId);

  if (shiftId && shiftQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftQuery} />;
  }

  return (
    <WorkFormDataProvider
      cancel={cancel}
      companies={companies}
      formMode={formMode}
      isHqAdmin={isHqAdmin}
      onSuccess={onSuccess}
      shiftData={shiftQuery?.data}
      shiftId={shiftId}
      selectBorrowerCompany={selectBorrowerCompany}
      ownCompanyId={ownCompanyId}
    />
  );
};

export const WorkFormDataProvider = ({
  cancel,
  companies,
  formMode,
  isHqAdmin,
  onSuccess,
  shiftData,
  shiftId,
  selectBorrowerCompany = false,
  ownCompanyId,
}: WorkFormDataProviderProps) => {
  // Form
  const formMethods = useForm<WorkFormContext>({
    defaultValues: {
      location: null,
      companyOption: null,
      createTab: 'single-day',
      multiDayDates: [],
      multiDayDayBlocks: [],
      ...mapShiftDetailToForm(shiftData),
    },
    shouldUnregister: false,
  });
  const selectedCompany = formMethods.watch('companyOption');
  const selectedCompanyId = selectedCompany?.value;
  const selectedDepartmentId = formMethods.watch('department');
  const selectedWorkTemplateId = formMethods.watch('jobId');
  const selectedJobTypeId = formMethods.watch('jobType');
  const isTempWork = formMethods.watch('isTempwork');
  const isFreelance = formMethods.watch('isFreelance');
  const certificates = formMethods.watch('jobCertificates');
  const startDate = formMethods.watch('startDate');
  const endDate = formMethods.watch('endDate');
  const startTime = formMethods.watch('startTime');
  const endTime = formMethods.watch('endTime');
  const shouldAdhereToDrivingRestRules =
    formMethods.watch('shouldAdhereToDrivingRestRules') ?? false;
  const nightShiftLimitationSystem = formMethods.watch('nightShiftLimitationSystem');

  const { site, isHQ, isOGP } = useSiteDetection();
  const showBorrowerPicker = selectBorrowerCompany && formMode === 'create';
  const isOwnCompanySelected = !!ownCompanyId && selectedCompanyId === ownCompanyId;
  const borrowerCompanyId =
    showBorrowerPicker && selectedCompanyId && !isOwnCompanySelected
      ? selectedCompanyId
      : undefined;
  const queryCompanyId = isOGP ? borrowerCompanyId : selectedCompanyId;
  const canLoadCompanyData = !!selectedCompanyId;
  const workTemplatesQuery = useGetWorkTemplates(site, queryCompanyId, canLoadCompanyData);
  const shiftCreateDataQuery = useGetWorkCreateData(site, queryCompanyId, canLoadCompanyData);
  const workTemplateDataQuery = useGetWorkTemplate(
    site,
    selectedWorkTemplateId,
    !!selectedWorkTemplateId
  );
  const isLimitedCompany = useHideLimitedCompanyShiftFields();
  const departmentDataQuery = useGetDepartmentDetail(
    site,
    selectedDepartmentId,
    !!selectedDepartmentId && !isLimitedCompany
  );
  const limitedCompanyDefaultDepartment = shiftCreateDataQuery.data;
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);

  const helperTexts = useMemo(
    () =>
      getHelperTexts(
        workTemplateDataQuery.data
          ? {
              breakMinutes: workTemplateDataQuery.data.breakMinutes,
              hourlyRateCents: isLimitedCompany
                ? undefined
                : workTemplateDataQuery.data.freelanceJob?.hourlyRateCents,
            }
          : undefined
      ),
    [
      isLimitedCompany,
      workTemplateDataQuery.data?.breakMinutes,
      workTemplateDataQuery.data?.freelanceJob?.hourlyRateCents,
    ]
  );

  const [company] = companies;
  useEffect(() => {
    if (showBorrowerPicker || !isOGP || !company) {
      return;
    }
    formMethods.setValue('companyOption', { value: company.id, label: company.name });
  }, [company?.id, isOGP, showBorrowerPicker]);

  const defaultValues = useMemo(() => {
    if (formMode === 'edit' || formMode === 'duplicate') {
      return mapShiftDetailToForm(shiftData);
    }
    const workTemplateDefaults = mapWorkTemplateToForm(workTemplateDataQuery.data);
    if (isLimitedCompany) {
      return applyLimitedCompanyDepartmentDefaults(
        workTemplateDefaults,
        limitedCompanyDefaultDepartment
      );
    }
    return workTemplateDefaults;
  }, [
    formMode,
    shiftData?.id,
    workTemplateDataQuery.data?.id,
    isLimitedCompany,
    limitedCompanyDefaultDepartment?.defaultDepartmentId,
  ]);

  const applicationsAndPlacementsEmploymentTypes: WorkerEmploymentType[] = useMemo(() => {
    if (formMode !== 'edit' || !shiftData) {
      return [];
    }

    const shiftApplicants = shiftData.applicants
      .map((a) => a.flexWorker?.employmentType)
      .filter((t): t is WorkerEmploymentType => !!t);

    const shiftClaimants = shiftData.claimants
      .map((c) => c.flexWorker?.employmentType)
      .filter((t): t is WorkerEmploymentType => !!t);

    return [...shiftApplicants, ...shiftClaimants];
  }, [formMode, shiftData]);

  const { title, subtitle } = useWorkFormTexts(formMode);
  const { showCreationTabs, creationType, setCreationType, isMultiDayCreation } =
    useWorkFormCreationTabs({ formMode, isOGP });

  const handleCreationTypeChange = (newCreationType: WorkFormCreationType) => {
    setCreationType(newCreationType);
    if (!showCreationTabs) {
      return;
    }

    const newTab: WorkFormCreateTab = newCreationType === 'multi-day' ? 'multi-day' : 'single-day';
    const currentTab = formMethods.getValues(MULTI_DAY_WORK_FORM_FIELDS.createTab) ?? 'single-day';
    changeWorkFormCreateTab(formMethods.setValue, currentTab, newTab);
  };

  const { validateBeforeSubmit: validateMultiDayBeforeSubmit } = useMultiDaySubmitValidation({
    setError: formMethods.setError,
  });

  const showCompanySelect = (isHQ && formMode === 'create') || showBorrowerPicker;
  const showTemplateSelect = formMode === 'create';
  const isSubmitDisabled = isMultiDayCreation
    ? (showCompanySelect && !selectedCompanyId) || (showTemplateSelect && !selectedWorkTemplateId)
    : (!selectedCompanyId && showCompanySelect) || (!selectedWorkTemplateId && showTemplateSelect);
  const disableOrtApplicableSwitches = !isHqAdmin && formMode === 'edit';

  const { onSubmit } = useWorkFormMutations({
    shiftId,
    formMode,
    defaultValues,
    cancel,
    onSuccess,
  });

  return (
    <FormContainer>
      <FormProvider {...formMethods}>
        <FullHeightForm
          name="work-form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            formMethods.handleSubmit((data) => {
              if (
                formMode === 'create' &&
                data.createTab === 'multi-day' &&
                !validateMultiDayBeforeSubmit(data)
              ) {
                return;
              }

              void onSubmit(data);
            })(e);
          }}
        >
          <FormBody>
            <Box>
              <Box>
                <Typography variant="h1" mb={4}>
                  {title}
                </Typography>
                <Typography variant="body1" mb={4}>
                  {subtitle}
                </Typography>
              </Box>
              {showCreationTabs && (!showBorrowerPicker || !!selectedCompanyId) ? (
                <WorkFormCreationTabs
                  creationType={creationType}
                  onCreationTypeChange={handleCreationTypeChange}
                />
              ) : null}
              <Divider />
              <WorkForm
                showCompanySelect={showCompanySelect}
                hideUntilCompanySelected={showBorrowerPicker}
                showTemplateSelect={showTemplateSelect}
                showCreateTabs={false}
                isContractFieldDisabled={
                  !isHqAdmin &&
                  formMode === 'edit' &&
                  !!shiftData?.claimants.filter((claimant) => claimant.state === 'accepted').length
                }
                allowDateBeforeToday={
                  isHqAdmin || formMode === 'create' || formMode === 'duplicate'
                }
                multiDayCalendarAllowsPast={isHqAdmin}
                companies={companies}
                workTemplates={workTemplatesQuery.data}
                shiftCreateData={shiftCreateDataQuery.data}
                defaultValues={defaultValues}
                departmentData={departmentDataQuery.data}
                showMultiplySection={formMode === 'create' || formMode === 'duplicate'}
                multiplySectionAllowsPast={isHqAdmin}
                helperTexts={helperTexts}
                disableOrtApplicableSwitches={disableOrtApplicableSwitches}
                applicationsAndPlacementsEmploymentTypes={applicationsAndPlacementsEmploymentTypes}
                shouldAdhereToDrivingRestRules={shouldAdhereToDrivingRestRules}
                nightShiftLimitationSystem={nightShiftLimitationSystem}
              />
            </Box>
          </FormBody>

          <SidebarActions>
            <LwButton onClick={cancel} color="secondary">
              Sluiten
            </LwButton>
            <LwButton color="primary" type="submit" disabled={isSubmitDisabled}>
              {formMode === 'edit'
                ? t('buttons.save', { ns: 'web-common' })
                : t('buttons.create', { ns: 'web-common' })}
            </LwButton>
          </SidebarActions>
        </FullHeightForm>
      </FormProvider>
    </FormContainer>
  );
};

const FormContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
}));

const FullHeightForm = styled('form')(() => ({
  height: '100%',
}));

const FormBody = styled(Box)(({ theme }) => ({
  width: '650px',
  overflow: 'auto',
  paddingRight: theme.spacing(2),
  flex: 1,
  height: 'calc(100% - 88px)',
  display: 'flex',
  flexDirection: 'column',
}));

const SidebarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(8),
}));
